/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, May 11th 2023, 5:14:37 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { APIMany2OneSelectFormInput, APIMany2OneEditButton, ConcreteAPIMany2OneSelectFormInputProps } from "ui-widgets";
import { AITaskInstruction } from "lib/schemas";
import { APISERVICEPATHS } from "lib/api";
import { COLORSCHEME } from "theme/styles";
import { RobotIcon } from "theme/icons";
import { StyledOSDialog, useToggleState, FormSubmitBubblingStopper } from "ui-widgets";
import { AITaskInstructionForm } from "./AITaskInstructionForm";
import { useOSFormContext } from "ui-widgets/forms/providers";

interface AITaskInstructionManyToOnePickerProps<TPrimaryObject>
  extends ConcreteAPIMany2OneSelectFormInputProps<TPrimaryObject, AITaskInstruction> {}

export const AITaskInstructionManyToOnePicker = <TPrimaryObject,>(
  props: AITaskInstructionManyToOnePickerProps<TPrimaryObject>
) => {
  const { aiTaskInstructionId, editIsOpen, handleCloseEdit, handleOpenEdit } = useAIInstructionMany2OnePicker(
    props.schema_field_key
  );

  return (
    <>
      <APIMany2OneSelectFormInput<TPrimaryObject, AITaskInstruction>
        label={props.label ?? "AI Task Instruction"}
        itemIdProperty="id"
        getOptionLabel={(option) => option.name}
        loadingText="Loading Instructions"
        apiServicePath={APISERVICEPATHS.aiTaskInstructions}
        inputStartAdornment={<RobotIcon fill={COLORSCHEME.primary} width={30} height={30} />}
        inputEndAdornment={<APIMany2OneEditButton objectId={aiTaskInstructionId} onClick={handleOpenEdit} />}
        renderRelatedObjectForm={({ updatePrimaryObject, closeNewFormFn, newFormOpenState }) => (
          <StyledOSDialog
            open={newFormOpenState}
            onClose={closeNewFormFn}
            popUpTitle="New AI Instruction"
            popUpIcon={<RobotIcon width={30} height={30} fill={COLORSCHEME.primary} />}
          >
            <FormSubmitBubblingStopper>
              <AITaskInstructionForm
                updateAiInstructionsFromAPICallback={(aiInstruction) => updatePrimaryObject(aiInstruction.id)}
                aiInstructionsDefaultValues={null}
                aiInstructionsFetchId={null}
              />
            </FormSubmitBubblingStopper>
          </StyledOSDialog>
        )}
        {...props}
      />

      <StyledOSDialog
        open={editIsOpen}
        onClose={handleCloseEdit}
        popUpTitle="Edit AI Instruction"
        popUpIcon={<RobotIcon width={30} height={30} fill={COLORSCHEME.primary} />}
      >
        <FormSubmitBubblingStopper>
          <AITaskInstructionForm
            updateAiInstructionsFromAPICallback={(aiInstruction) => {}}
            aiInstructionsDefaultValues={null}
            aiInstructionsFetchId={aiTaskInstructionId}
          />
        </FormSubmitBubblingStopper>
      </StyledOSDialog>
    </>
  );
};

const useAIInstructionMany2OnePicker = <TPrimaryObject,>(
  schema_field_key: ConcreteAPIMany2OneSelectFormInputProps<TPrimaryObject, AITaskInstruction>["schema_field_key"]
) => {
  const { watch } = useOSFormContext<TPrimaryObject>();

  const aiTaskInstructionId = watch<any>(schema_field_key) as AITaskInstruction["id"];

  const { handleClose: handleCloseEdit, handleOpen: handleOpenEdit, open: editIsOpen } = useToggleState();

  return { aiTaskInstructionId, handleCloseEdit, handleOpenEdit, editIsOpen };
};
